import { css } from '@emotion/css';
import { cssObj } from '@/css/resources/functions.css';
import { resetFirstLastMargins } from '@/css/resources/placeholders.css';
import {
	fontFamilies,
	fontWeights,
	transitions,
} from '@/css/resources/variables.css';
import { expandLink, fluidValues, fontStyle } from '@/css/resources/mixins.css';
import {
	imageExpandTransition,
	imageReduceTransition,
	linkNoUnderlineWithHover,
} from '@/css/placeholders.css';

const getStyles = () =>
	css({
		overflow: 'hidden',
		maxWidth: '100%',
		color: '#000',

		'.supt-card__wrapper': {
			position: 'relative',
		},

		'.supt-cardShow__imageWrapper': cssObj(
			fluidValues('marginBottom', 24, 32),
			{
				aspectRatio: '768/590',
				display: 'flex',
				position: 'relative',
				overflow: 'hidden',
				marginRight: 'auto',

				'.supt-cardShow__label': cssObj(fluidValues('top', 8, 10), {
					position: 'absolute',
					left: '50%',
					transform: 'translateX(-50%)',
					zIndex: 2,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					width: '90%',

					'&__title, &__text': cssObj(
						fontStyle({
							fontFamily: fontFamilies.secondary,
							fontSize: [13, 14],
							lineHeight: 1.28,
							letterSpacing: -0.3,
							textAlign: 'center',
						}),
						{
							color: '#000',
							padding: '8px',
							backgroundColor:
								'var(--color-section-background, #fff)',
							borderRadius: 4,
							backdropFilter: 'blur(4px)',
						}
					),

					'&__title': {
						fontWeight: fontWeights.bold,
						textTransform: 'uppercase',
					},
					'&__text': {
						fontWeight: fontWeights.normal,
						marginTop: -4,

						span: {
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							flexBasis: '100%',
						},
					},
				}),

				'.supt-figure': {
					display: 'inline-flex',
					objectFit: 'cover',

					span: {
						width: '100% !important',
						height: 'auto !important',
					},

					img: {
						objectFit: 'cover',
					},
				},

				'.supt-cardShow__categories': cssObj(
					fluidValues('bottom', 8, 10),
					fluidValues('left', 8, 10),
					{
						position: 'absolute',
						display: 'flex',
						gap: '5px',
					}
				),
			}
		),

		'.supt-cardShow__artist': cssObj(
			fontStyle({
				fontFamily: fontFamilies.primary,
				fontWeight: fontWeights.normal,
				fontSize: [32, 34],
				lineHeight: [28, 30],
				letterSpacing: [0.2, 0.2],
				textTransform: 'uppercase',
				textAlign: 'left',
			}),
			{
				wordBreak: 'break-word',
				overflowWrap: 'anywhere',
			}
		),

		'.supt-cardShow__link': cssObj(resetFirstLastMargins, {
			display: 'block',
			textDecoration: 'none',

			'&:empty': {
				marginBottom: 0,

				'& + .supt-cardShow__title': {
					marginTop: 0,
				},
			},
		}),

		'.supt-cardShow__title': cssObj(
			fontStyle({
				fontFamily: fontFamilies.tertiary,
				fontWeight: fontWeights.normal,
				fontSize: [19, 24],
				lineHeight: [23, 29],
				letterSpacing: [-0.4, -0.6],

				textTransform: '',
				textAlign: 'left',
			}),
			fluidValues('marginTop', 4, 5),
			{
				fontStyle: 'italic',
			}
		),

		'.supt-cardShow__subtitle': cssObj(
			fontStyle({
				fontFamily: fontFamilies.secondary,
				fontWeight: fontWeights.normal,
				fontSize: [13, 16],
				lineHeight: 1.375,
				textTransform: '',
				textAlign: 'left',
			}),
			fluidValues('marginTop', 2, 3),
			fluidValues('marginBottom', 12, 15),
			{
				letterSpacing: '-0.5px',
			}
		),

		'.supt-cardShow__details': cssObj(fluidValues('marginTop', 7, 8), {
			p: cssObj(
				fontStyle({
					name: 'Text S Default',
					fontFamily: fontFamilies.secondary,
					fontWeight: fontWeights.normal,
					fontSize: [15, 16],
					lineHeight: [21, 22],
					letterSpacing: [-0.4, -0.5],
					textTransform: 'none',

					textAlign: 'left',
				})
			),
		}),

		'.supt-cardShow__public-list': cssObj(
			fluidValues('marginTop', 8, 12),
			fluidValues('gap', 10, 12),
			{
				listStyle: 'none',
				padding: 0,
				display: 'flex',
				position: 'relative',

				li: {
					display: 'flex',
					alignItems: 'center',

					'figure, span': {
						width: '100%',
						maxHeight: '22px',
					},

					'&.supt-cardShow__public-extra': cssObj(
						fluidValues('fontSize', 15, 16),
						{
							letterSpacing: '-0.03125em',
							lineHeight: '1.375em',
						}
					),
				},
			}
		),

		'.supt-cardShow__ticket-wrapper': cssObj(
			fluidValues('height', 36, 38),
			{
				marginTop: 20,
			}
		),

		'.supt-cardShow__ticket': {
			position: 'absolute',
			left: 0,
			bottom: 0,
			zIndex: '2',

			'.supt-cardShow__ticket__button.supt-buttonPrimary': cssObj(
				fluidValues('width', 36, 38),
				fluidValues('height', 36, 38),
				{
					minWidth: 'auto !important',
					margin: 0,
					zIndex: '2',
					display: 'flex',
					transition: `all ${transitions.mid} ease-out`,
					float: 'left',
					whiteSpace: 'nowrap',

					'&:hover': {
						width: '100%',
						color: '#ffffff',
						backgroundColor: '#000',

						'.supt-button__inner': {
							transform: 'translateX(0)',
						},
					},

					'&::before': cssObj(
						fluidValues('width', 36, 38),
						fluidValues('height', 36, 38),
						fluidValues('padding', 4, 5),
						{
							backgroundColor: '#000',
							color: '#fff',
							content: `url("data:image/svg+xml,%3Csvg width='26' height='26' viewBox='0 0 26 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M17.447 3.022a4.328 4.328 0 0 0-1.315 2.902c-.044 1.054.32 2.087 1.088 2.856.769.769 1.802 1.132 2.856 1.088a4.328 4.328 0 0 0 2.869-1.281l1.65 1.585-14.423 14.424-1.685-1.685c.484-.728.735-1.52.767-2.284.044-1.052-.321-2.056-1.073-2.808-.769-.768-1.802-1.132-2.856-1.088a4.28 4.28 0 0 0-2.252.766l-1.67-1.669L15.829 1.404Z' stroke='%23FFF' stroke-width='1.6'/%3E%3Cpath fill='%23FFF' d='m11.495 7.905 1.13 1.132-1.13 1.131-1.132-1.131zM13.333 9.744l1.131 1.131-1.131 1.132-1.131-1.132zM15.172 11.582l1.13 1.132-1.13 1.131-1.132-1.131zM17.01 13.42l1.131 1.132-1.131 1.131-1.131-1.13z'/%3E%3C/g%3E%3C/svg%3E")`,
							borderRadius: '50%',
							position: 'absolute',
							right: '0px',
							top: '0px',
							bottom: '0px',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'flex-start',
							border: '1px solid #000',
							boxSizing: 'border-box',
						}
					),

					'.supt-button__inner': {
						margin: 0,
						overflow: 'visible',
						display: 'flex',
						alignItems: 'center',
						transition: `transform ${transitions.mid} ease-out`,
						transform: 'translateX(calc(-100% + 10px))',

						'&::before': {
							content: "''",
							width: '30px',
							height: '100%',
						},
					},
				}
			),
		},

		/* with link */
		'&.supt-withLink': cssObj(expandLink(), {
			transition: `all ${transitions.mid} ease-out 0.1s`,

			'.supt-cardShow__image': cssObj(imageReduceTransition),

			'.supt-cardShow__link, .supt-cardShow__link h3': cssObj(
				linkNoUnderlineWithHover,
				{
					backgroundPosition: '0 1em',
				}
			),

			'&:hover, &:focus-within': {
				'.supt-cardShow__image': cssObj(imageExpandTransition),
			},
		}),

		'&.-canceled, &.-postponed, &.-closed': {
			'.supt-cardShow__details': {
				'.supt-cardShow__day': {
					display: 'flex',
					alignItems: 'center',

					'.supt-cardShow__day__info': {
						textDecorationLine: 'line-through',
					},

					'.supt-tag[data-type="alert"]': {
						marginLeft: '7px',
					},
				},
			},
		},

		'&.-portrait': {
			'.supt-cardShow__imageWrapper': {
				aspectRatio: '768/1047',
			},
		},
		'&.-highlighted': {
			backgroundColor: '#000',
			color: '#fff',
			padding: 20,

			'.supt-cardShow__ticket': {
				left: 0,
				overflow: 'hidden',

				'.supt-cardShow__ticket__button.supt-buttonPrimary': {
					backgroundColor: '#fff',
					color: '#000',
					borderColor: '#fff',

					'&::before': {
						backgroundColor: '#fff',
						color: '#000',
						borderColor: '#fff',
						content: `url("data:image/svg+xml,%3Csvg width='26' height='26' viewBox='0 0 26 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M17.447 3.022a4.328 4.328 0 0 0-1.315 2.902c-.044 1.054.32 2.087 1.088 2.856.769.769 1.802 1.132 2.856 1.088a4.328 4.328 0 0 0 2.869-1.281l1.65 1.585-14.423 14.424-1.685-1.685c.484-.728.735-1.52.767-2.284.044-1.052-.321-2.056-1.073-2.808-.769-.768-1.802-1.132-2.856-1.088a4.28 4.28 0 0 0-2.252.766l-1.67-1.669L15.829 1.404Z' stroke='%23000' stroke-width='1.6'/%3E%3Cpath fill='%23000' d='m11.495 7.905 1.13 1.132-1.13 1.131-1.132-1.131zM13.333 9.744l1.131 1.131-1.131 1.132-1.131-1.132zM15.172 11.582l1.13 1.132-1.13 1.131-1.132-1.131zM17.01 13.42l1.131 1.132-1.131 1.131-1.131-1.13z'/%3E%3C/g%3E%3C/svg%3E")`,
					},

					'&:hover': {
						backgroundColor: '#fff',
					},

					'.supt-button__inner': {
						borderColor: '#fff',
					},
				},
			},

			'&.-light': {
				backgroundColor: '#fff',
				color: '#000',

				'.supt-cardShow__ticket': {
					'.supt-cardShow__ticket__button.supt-buttonPrimary': {
						backgroundColor: '#000',
						borderColor: '#000',
						color: '#fff',

						'&::before': {
							backgroundColor: '#000',
							color: '#fff',
							borderColor: '#000',
							content: `url("data:image/svg+xml,%3Csvg width='26' height='26' viewBox='0 0 26 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M17.447 3.022a4.328 4.328 0 0 0-1.315 2.902c-.044 1.054.32 2.087 1.088 2.856.769.769 1.802 1.132 2.856 1.088a4.328 4.328 0 0 0 2.869-1.281l1.65 1.585-14.423 14.424-1.685-1.685c.484-.728.735-1.52.767-2.284.044-1.052-.321-2.056-1.073-2.808-.769-.768-1.802-1.132-2.856-1.088a4.28 4.28 0 0 0-2.252.766l-1.67-1.669L15.829 1.404Z' stroke='%23FFF' stroke-width='1.6'/%3E%3Cpath fill='%23FFF' d='m11.495 7.905 1.13 1.132-1.13 1.131-1.132-1.131zM13.333 9.744l1.131 1.131-1.131 1.132-1.131-1.132zM15.172 11.582l1.13 1.132-1.13 1.131-1.132-1.131zM17.01 13.42l1.131 1.132-1.131 1.131-1.131-1.13z'/%3E%3C/g%3E%3C/svg%3E")`,
						},

						'&:hover': {
							backgroundColor: '#000',
						},

						'.supt-button__inner': {
							borderColor: '#000',
						},
					},
				},
			},
		},
	});

export { getStyles };
